import React, { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { ChevronDown, ChevronUp, TrendingUp, MapPin, Calendar, Target } from 'lucide-react';

const TipCard = ({ tip, onPurchase, canPurchase, isPurchased }) => {
  const [expanded, setExpanded] = useState(false);

  const getRiskColor = (risk) => {
    switch (risk) {
      case 'low':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'medium':
        return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
      case 'high':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-gray-500/20 text-gray-400 border-gray-500/30';
    }
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'won':
        return 'bg-green-500/20 text-green-400 border-green-500/30';
      case 'lost':
        return 'bg-red-500/20 text-red-400 border-red-500/30';
      default:
        return 'bg-blue-500/20 text-blue-400 border-blue-500/30';
    }
  };

  const formatDate = (dateString) => {
    const date = new Date(dateString);
    return date.toLocaleDateString('de-DE', { 
      day: '2-digit', 
      month: '2-digit', 
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div 
      data-testid={`tip-card-${tip.id}`}
      className="bg-[#1E1E1E] border border-white/10 rounded-xl overflow-hidden hover:border-[#00BFA6]/50 transition-all duration-300 card-hover"
    >
      {/* Header */}
      <div className="p-6 border-b border-white/10">
        <div className="flex justify-between items-start mb-4">
          <div>
            <div className="flex items-center gap-2 mb-2">
              <Badge className="bg-[#00BFA6]/20 text-[#00BFA6] border-[#00BFA6]/30">
                {tip.sport}
              </Badge>
              <Badge variant="outline" className="text-gray-400">
                {tip.league}
              </Badge>
            </div>
            <h3 className="text-xl font-bold">
              {tip.team_home} <span className="text-gray-500">vs</span> {tip.team_away}
            </h3>
          </div>
          
          <div className="text-right">
            <div className="font-mono text-2xl font-bold text-[#00BFA6]" data-testid={`tip-odds-${tip.id}`}>
              {tip.best_odds}
            </div>
            <div className="text-xs text-gray-400">{tip.bookmaker}</div>
          </div>
        </div>

        <div className="flex items-center gap-4 text-sm text-gray-400">
          <div className="flex items-center gap-1">
            <Calendar className="w-4 h-4" />
            {formatDate(tip.match_date)}
          </div>
          {tip.venue && (
            <div className="flex items-center gap-1">
              <MapPin className="w-4 h-4" />
              {tip.venue}
            </div>
          )}
        </div>
      </div>

      {/* Prediction & Confidence */}
      <div className="p-6 bg-[#121212]">
        <div className="flex justify-between items-center mb-4">
          <div>
            <div className="text-sm text-gray-400 mb-1">Vorhersage</div>
            <div className="text-lg font-bold">{tip.prediction.replace('_', ' ').toUpperCase()}</div>
          </div>
          
          <div className="text-right">
            <div className="text-sm text-gray-400 mb-1">Vertrauen</div>
            <div className="flex items-center gap-2">
              <div className="font-mono text-2xl font-bold text-[#00BFA6]">{tip.confidence}%</div>
              <Target className="w-5 h-5 text-[#00BFA6]" />
            </div>
          </div>
        </div>

        <div className="flex gap-2">
          <Badge className={getRiskColor(tip.risk_level)}>
            Risiko: {tip.risk_level.toUpperCase()}
          </Badge>
          <Badge className={getStatusColor(tip.status)}>
            Status: {tip.status.toUpperCase()}
          </Badge>
        </div>
      </div>

      {/* Analysis (Expandable) */}
      {isPurchased && (
        <div className="border-t border-white/10">
          <button
            onClick={() => setExpanded(!expanded)}
            className="w-full p-4 flex justify-between items-center hover:bg-white/5 transition-colors"
            data-testid={`expand-analysis-btn-${tip.id}`}
          >
            <span className="font-medium">Detaillierte Analyse</span>
            {expanded ? <ChevronUp className="w-5 h-5" /> : <ChevronDown className="w-5 h-5" />}
          </button>

          {expanded && tip.analysis && (
            <div className="p-6 bg-[#121212] space-y-4" data-testid={`analysis-content-${tip.id}`}>
              <div>
                <h4 className="text-sm font-bold text-[#00BFA6] mb-2">Form Heimmannschaft</h4>
                <p className="text-sm text-gray-300">{tip.analysis.form_home}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-bold text-[#00BFA6] mb-2">Form Auswärtsteam</h4>
                <p className="text-sm text-gray-300">{tip.analysis.form_away}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-bold text-[#00BFA6] mb-2">Direktbegegnungen</h4>
                <p className="text-sm text-gray-300">{tip.analysis.head_to_head}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-bold text-[#00BFA6] mb-2">Verletzungen & Sperren</h4>
                <p className="text-sm text-gray-300">{tip.analysis.injuries}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-bold text-[#00BFA6] mb-2">Wetter</h4>
                <p className="text-sm text-gray-300">{tip.analysis.weather}</p>
              </div>
              
              <div>
                <h4 className="text-sm font-bold text-[#00BFA6] mb-2">Gesamteinschätzung</h4>
                <p className="text-sm text-gray-300">{tip.analysis.overall_reasoning}</p>
              </div>
              
              <div className="pt-4 border-t border-white/10">
                <div className="flex items-center gap-2 text-xs text-gray-400">
                  <TrendingUp className="w-4 h-4" />
                  Analysiert von: {tip.ai_models_used?.join(', ')}
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* Actions */}
      {!isPurchased && (
        <div className="p-4 border-t border-white/10">
          <Button
            onClick={() => onPurchase(tip.id)}
            disabled={!canPurchase}
            className="w-full bg-[#00BFA6] hover:bg-[#009E8A] text-black font-bold py-3 rounded-lg transition-all disabled:opacity-50 disabled:cursor-not-allowed"
            data-testid={`purchase-tip-btn-${tip.id}`}
          >
            {canPurchase ? 'Tipp kaufen (1 Tipp)' : 'Keine Tipps verfügbar'}
          </Button>
        </div>
      )}
    </div>
  );
};

export default TipCard;
